import Dinero from "dinero.js";
import { useEffect, useState } from "react";

import { useApiClient } from "../../api-client";
import { useEmployment } from "../home/useEmployment";
import { PayrollCycle } from "./usePayrollCycle";

export enum TransactionType {
  REFUND = "refund",
  PURCHASE = "purchase",
  ACCUMULATION = "accumulation-delivery",
  ALLOWANCE_DELIVERY = "allowance-delivery",
  WITHDRAWAL = "withdrawal",
}

export enum RejectedReason {
  OUTSIDE_SCHEDULE = "outside-schedule",
  AMOUNT_NOT_ALLOWED = "amount-not-allowed",
  OUTSIDE_TIME_SCHEDULE = "outside-time-schedule",
  INSUFFICIENT_COMPANY_BALANCE = "insufficient-company-balance",
  SAVINGS_LIMIT = "savings-limit",
  INVALID_MERCHANT = "invalid-merchant",
  FLEX_PAYROLL_LIMIT = "flex-payroll-limit",
  IN_KIND_YEAR_LIMIT = "in-kind-year-limit",
  MEALS_PAYROLL_LIMIT = "meals-payroll-limit",
  TRANSPORT_PAYROLL_LIMIT = "transport-payroll-limit",
  MEALS_YEAR_LIMIT = "meals-year-limit",
  EDUCATION_YEAR_LIMIT = "education-year-limit",
  YEAR_LIMIT = "year-limit",
  NURSERY_YEAR_LIMIT = "nursery-year-limit",
  TRANSPORT_YEAR_LIMIT = "transport-year-limit",
  MEALS_DAILY_LIMIT = "meals-daily-limit",
  EMPLOYEE_NOT_ACTIVE = "employee-not-active",
  BLOCKED_CARD = "blocked-card",
  OUTSIDE_SPAIN = "outside-spain",
  LOCKED_FLEX = "locked-flex",
  FLEX_NOT_ALLOWED = "flex-not-allowed",
  MAX_EXEMPT_DAY = "max-exempt-day",
  MAX_EXEMPT_PAYROLL = "max-exempt-payroll",
  MAX_EXEMPT_YEAR = "max-exempt-year",
  MAX_SALARY_IN_KIND_YEAR = "max-salary-in-kind-year",
  MAX_SALARY_REDUCTION_PAYROLL = "max-salary-reduction-payroll",
  MAX_PRORATED_EXEMPT_PAYROLL = "max-prorated-exempt-payroll",
  MAX_PRORATED_EXEMPT_YEAR = "max-prorated-exempt-year",
  NOT_EXEMPT_NOT_ALLOWED = "not-exempt-not-allowed",
  OUTSIDE_ENGAGEMENT_PERIOD = "outside-engagement-period",
  INSURED_ID_NOT_FOUND = "insured-id-not-found",
  SIGN_UP_HEALTH_CLIENT_ERROR = "sign-up-health-client-error",
  REMOVE_HEALTH_CLIENT_ERROR = "remove-health-client-error",
  RECURRENT_PAYMENT_NOT_FOUND = "recurrent-payment-not-found",
  HIRED_HEALTH_INSURANCE = "hired-health-insurance",
  TOO_FAR_BACK = "too-far-back",
  TOO_FAR_BACK_NURSERY = "too-far-back-nursery",
  INCORRECT_ENGAGEMENT_PERIOD = "incorrect-engagement-period",
  DUPLICATED_EMAIL = "duplicated-email",
  OLDER_CHILD = "older-child",
  AUTO_ON_BOARDING_COMPANY_WITHOUT_RECHARGE = "auto-on-boarding-company-without-recharge",
  NO_INSURANCE_PRODUCTS = "no-insurance-products",
  RESULTING_IN_NEGATIVE_AMOUNT = "resulting-in-negative-amount",
  UNACQUIRED_NURSERY = "unacquired-nursery",
  INVALID_HASH = "invalidHash",
  MISSING_HASH = "missingHash",
  MISSING_STATUS = "missingStatus",
  MISSING_ACTION = "missingAction",
  INVALID_ACTION = "invalidAction",
  INVALID_STATUS = "invalidStatus",
  MISSING_EFFECTIVE_DATE = "missingEffectiveDate",
  INVALID_EFFECTIVE_DATE = "invalidEffectiveDate",
  INSURED_INVALID_HASH = "insuredInvalidHash",
  ERROR_HEALTH_INSURANCE_REQUEST = "errorHealthInsuranceRequest",
  NURSERY_NOT_ACQUIRED = "nursery-not-acquired",
  AGE_LIMIT = "age-limit",
  REJECTED_BY_AUTHORIZER = "rejected-by-authorizer",
  ALL_CONTRIBUTIONS_MUST_NOT_EXCEED_TAX_LIMIT = "all-contributions-must-not-exceed-tax-limit",
  EMPLOYEE_CONTRIBUTION_MUST_NOT_EXCEED_COMPANY = "employee-contribution-must-not-exceed-company",
  NO_RULES_FOR_TRANSACTION = "no-rules-for-transaction",
  MISSING_CAR_INFORMATION = "missing-car-information",
}

export enum FundsOrigin {
  FROM_BENEFIT_BAG = "benefit-bag",
  FROM_SALARY = "salary",
  FROM_COMPANY = "company",
  FROM_ACCUMULATED_SALARY = "accumulated-salary",
  FROM_ACCUMULATED_ALLOWANCE = "accumulated-allowance",
}
export enum PurchaseStatus {
  CONFIRMED = "confirmed",
  REJECTED = "rejected",
  PENDING = "pending",
  CANCELLED = "cancelled",
}

export interface MerchantData {
  name: string;
  imageUrl?: string;
  brand?: string;
}
export enum BenefitCategories {
  MEAL = "meal",
  TRANSPORT = "transport",
  NURSERY = "nursery",
  TRAINING = "training",
  DISCOUNT = "discount",
  IT_PRODUCT = "it-product",
  GYM = "gym-benefit",
  GROCERIES = "groceries",
  HEALTH_INSURANCE = "health-insurance",
  RENTING = "renting",
  LIFE_INSURANCE = "life-insurance",
  WELLNESS = "wellness",
  UNKNOWN = "unknown",
  RETIREMENT_INSURANCE = "retirement-insurance",
  PENSION_PLAN = "pension-plan",
  GAS = "gas",
  ALL_EXPENSES = "all-expenses",
  RESTAURANTS = "restaurants",
  HOME_OFFICE = "home-office",
}

export type Transaction = {
  id: string;
  type: TransactionType;
  merchant?: MerchantData;
  state: PurchaseStatus;
  rejectedReason: RejectedReason | null;
  fundOrigins: FundsOrigin[];
  amount: Money;
  taxExemption: Money | null;
  userTransactionDate: Date;
  benefit?: BenefitCategories;
};

enum SupportedCurrency {
  EURO = "EUR",
  MEXICAN_PESO = "MXN",
}

type MoneyValue = {
  amountInCents: number;
  currency?: SupportedCurrency;
};

class Money {
  amountInCents: number;

  currency: SupportedCurrency;

  constructor(amountDto: MoneyValue) {
    this.amountInCents = amountDto.amountInCents ?? 0;
    this.currency = amountDto.currency ?? SupportedCurrency.EURO;
  }

  toFormatString(locale: string) {
    return this.toDineroInstance().setLocale(locale).toFormat("$0,0.00");
  }

  private toDineroInstance(): Dinero.Dinero {
    return Dinero({
      amount: this.amountInCents,
      currency: this.currency?.toUpperCase() as Dinero.Currency,
    });
  }
}
const restoreTransaction = (transaction: any): Transaction => {
  return {
    id: transaction.id,
    type: transaction.type,
    merchant: transaction.merchant
      ? {
          name: transaction.merchant.name,
          imageUrl: transaction.merchant.imageUrl,
          brand: transaction.merchant.brand,
        }
      : undefined,
    state: transaction.state,
    rejectedReason: transaction.rejectedReason,
    fundOrigins: transaction.fundOrigins,
    amount: new Money(transaction.originalAmount),
    taxExemption: transaction.taxExemption
      ? new Money(transaction.taxExemption)
      : null,
    userTransactionDate: new Date(transaction.userTransactionIsoTime),
    benefit: transaction.benefit,
  };
};

const useTransactions = (payrollCycle: PayrollCycle | null) => {
  const { get } = useApiClient();
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const { employmentId } = useEmployment();

  const getTransactions = async () => {
    const { data } = await get(
      `/v2/employees/${employmentId}/payrolls/${payrollCycle?.id}/transactions`
    );

    setTransactions(data.content.transactions.map(restoreTransaction));
    setIsLoading(false);
  };

  useEffect(() => {
    if (!payrollCycle || !employmentId) return;
    getTransactions();
  }, [payrollCycle, employmentId]);

  return { isLoading, transactions };
};

export { useTransactions };
